<template>
  <div class="card">
    <div class="card-body">
      <div class="border rounded p-1">
        <b-row>
          <b-col cols="12" lg="6" md="6" class="mb-1">
          </b-col>
          <b-col cols="12" lg="6" md="6" class="mb-1">
            <label class="input-label form-label">{{ $t("customer") }}</label>
            <MultiSelectInput
              v-model="selectedCompany"
              :options="availableCompanies"
              label="companyName"
              trackBy="id"
              :key="selectedCompany"
              moduleName="customers"
              :multiple="false"
              @input="onCompanySelect"
            >
              <template #option="{ props }">
                <div>
                  <p>{{ props.option.companyNumber }} - {{ props.option.companyName }}</p>
                </div>
              </template>
            </MultiSelectInput>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="12" lg="6" md="6">
            <div class="table-responsive company-info-table">
              <table class="table table-bordered mb-0 text-white">
                <tbody>
                  <tr>
                    <td class="font-weight-bold">{{ $t("companyName") }}</td>
                    <td>{{ company.companyName || "-" }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">{{ $t("cases") }}</td>
                    <td>{{ company.casesCount || 0 }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">{{ $t("users") }}</td>
                    <td>{{ usersCount || 0 }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">{{ $t("registeredAt") }}</td>
                    <td>{{ $dateFormatter(company.createdAt) || "" }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-col>
          <b-col v-if="selectedCompany" cols="12" lg="6" md="6">
            <div class="table-responsive company-info-table">
              <table class="table table-bordered mb-0 text-white">
                <tbody>
                  <tr>
                    <td class="font-weight-bold">{{ $t("companyName") }}</td>
                    <td>{{ selectedCompany && selectedCompany.companyName ? selectedCompany.companyName : "" }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">{{ $t("cases") }}</td>
                    <td>{{ selectedCompany && selectedCompany.totalCases ? selectedCompany.totalCases : 0 }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">{{ $t("users") }}</td>
                    <td>{{ selectedCompanyUsersCount }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">{{ $t("registeredAt") }}</td>
                    <td>{{ $dateFormatter(selectedCompany.createdAt) || "" }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-col>
        </b-row>
        <b-row v-if="selectedCompany" class="mt-1">
          <b-col cols="12" lg="6" md="6" class="mb-1">
            <b-button variant="primary" block @click="onMergeSelectedIntoCurrent">
              {{ $t("mergeInto") }} {{ company.companyName }}
            </b-button>
          </b-col>
          <b-col cols="12" lg="6" md="6" class="mb-1">
            <b-button variant="primary" block @click="onMergeCurrentIntoSelected">
              {{ $t("mergeInto") }} {{ selectedCompany.companyName }}
            </b-button>
          </b-col>
        </b-row>
      </div>
    </div>
  </div>
</template>

<script>
import MultiSelectInput from "@/components/MultiSelectInput.vue";
import { mapGetters } from "vuex";

export default {
  components: {
    MultiSelectInput,
  },
  props: {
    company: {
      type: Object,
      default() {
        return {};
      },
    },
    usersCount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      selectedCompany: null,
      selectedCompanyUsersCount: "",
    };
  },
  computed: {
    ...mapGetters("customers", ["customers"]),
    availableCompanies() {
      return this.customers.filter((item) => String(item.id) !== String(this.company.id));
    },
  },
  async created() {
    await this.$store.dispatch("customers/list");
  },
  methods: {
    async updateUsersFromMergedToMerger(mergedId, mergerId) {
      const responseUser = await this.$store.dispatch("users/list", {
        company_id: mergedId,
      });
      const users = responseUser && responseUser.data && responseUser.data.data ? responseUser.data.data : [];
      const updatePromises = users.map((user) =>
        this.$store.dispatch("users/update", {
          id: user.id,
          company_id: mergerId,
          set_company_id: mergerId,
        })
      );
      await Promise.all(updatePromises);
    },
    async onCompanySelect() {
      if (!this.selectedCompany || !this.selectedCompany.id) {
        this.selectedCompanyUsersCount = "";
        return;
      }
      try {
        this.$store.commit("showLoader", true);
        const responseUser = await this.$store.dispatch("users/list", {
          company_id: this.selectedCompany.id,
        });
        this.$store.commit("showLoader", false);
        this.selectedCompanyUsersCount = responseUser && responseUser.data ? responseUser.data.count : "";
      } catch (e) {
        this.$store.commit("showLoader", false);
        this.selectedCompanyUsersCount = "";
      }
    },
    async onMergeSelectedIntoCurrent() {
      if (!this.selectedCompany || !this.selectedCompany.id || !this.company || !this.company.id) {
        return;
      }
      const confirmed = await this.showConfirmationDialog({
        title: this.$t("doYouWantToMergeCompanies", {
          sourceCompany: this.selectedCompany.companyName,
          targetCompany: this.company.companyName,
        }),
        subText: this.$t("mergeCannotBeUndone"),
      });
      if (!confirmed) {
        return;
      }
      const mergerId = String(this.company.id);
      const mergedId = String(this.selectedCompany.id);
      this.$store.commit("showLoader", true);
      try {
        await this.updateUsersFromMergedToMerger(mergedId, mergerId);
        await this.$store.dispatch("customers/mergeCompanies", {
          mergerId,
          mergedId,
        });
      } finally {
        this.selectedCompany = null;
        this.selectedCompanyUsersCount = "";
        await this.$store.dispatch("customers/list");
        this.$store.commit("showLoader", false);
      }
    },
    async onMergeCurrentIntoSelected() {
      if (!this.selectedCompany || !this.selectedCompany.id || !this.company || !this.company.id) {
        return;
      }
      const confirmed = await this.showConfirmationDialog({
        title: this.$t("doYouWantToMergeCompanies", {
          sourceCompany: this.company.companyName,
          targetCompany: this.selectedCompany.companyName,
        }),
        subText: this.$t("mergeCannotBeUndone"),
      });
      if (!confirmed) {
        return;
      }
      const mergerId = String(this.selectedCompany.id);
      const mergedId = String(this.company.id);
      this.$store.commit("showLoader", true);
      try {
        await this.updateUsersFromMergedToMerger(mergedId, mergerId);
        await this.$store.dispatch("customers/mergeCompanies", {
          mergerId,
          mergedId,
        });
      } finally {
        this.$router.push({ name: "customers" });
      }
    },
  },
};
</script>

<style lang="scss">
.company-info-table table,
.company-info-table td {
  color: #000000 !important;
}

body.dark-layout {
  .company-info-table table,
  .company-info-table td {
    color: #fff !important;
  }
}
</style>